import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

export default function SafariExperiencesCarousel({ experiences, currentExperience }) {
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)
    const scrollContainerRef = useRef(null)
    const autoPlayIntervalRef = useRef(null)

    // Filter out the current experience from the list
    const filteredExperiences = experiences.filter(exp => exp.slug !== currentExperience)

    // Auto-scroll functionality
    useEffect(() => {
        if (!isAutoPlaying || !scrollContainerRef.current) return

        autoPlayIntervalRef.current = setInterval(() => {
            const container = scrollContainerRef.current
            if (container) {
                // Calculate card width + gap (assuming 2 cards visible on desktop)
                const cardWidth = 400 // approximate width of each card with gap
                const maxScroll = container.scrollWidth - container.clientWidth

                if (container.scrollLeft >= maxScroll - 10) {
                    // If at the end, scroll back to start
                    container.scrollTo({ left: 0, behavior: 'smooth' })
                } else {
                    // Scroll to next card
                    container.scrollBy({ left: cardWidth, behavior: 'smooth' })
                }
            }
        }, 5000) // Change slide every 5 seconds

        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current)
            }
        }
    }, [isAutoPlaying])

    const scroll = (direction) => {
        const container = scrollContainerRef.current
        if (container) {
            const scrollAmount = direction === 'left' ? -400 : 400
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
            setIsAutoPlaying(false) // Pause auto-play when user manually scrolls
        }
    }

    if (filteredExperiences.length === 0) return null

    return (
        <div className="relative">
            {/* Navigation Arrows */}
            <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-safari-green hover:text-white text-safari-green rounded-full p-3 shadow-lg transition-all duration-300 z-10 group hidden md:block"
                aria-label="Previous safari experience"
            >
                <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                    />
                </svg>
            </button>

            {/* Scrollable Container */}
            <div
                ref={scrollContainerRef}
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 md:px-0"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
            >
                {filteredExperiences.map((experience, index) => (
                    <Link
                        key={`${experience.slug}-${index}`}
                        to={experience.link}
                        className="flex-shrink-0 card group flex flex-col h-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                        style={{ width: '100%', minWidth: '280px', maxWidth: '400px' }}
                    >
                        <div className="relative h-64 overflow-hidden flex-shrink-0">
                            <img
                                src={experience.image}
                                alt={experience.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute top-4 right-4 bg-safari-gold text-white px-3 py-1 rounded-full text-sm font-semibold">
                                {experience.duration}
                            </div>
                        </div>
                        <div className="p-6 flex flex-col flex-grow">
                            <h3 className="text-xl font-bold mb-3 text-safari-green line-clamp-2 min-h-[56px]">
                                {experience.title}
                            </h3>
                            <p className="text-gray-600 mb-4 text-sm flex-grow line-clamp-3">{experience.description}</p>
                            <div className="mb-4 min-h-[60px]">
                                {experience.highlights.slice(0, 3).map((highlight, i) => (
                                    <span
                                        key={i}
                                        className="inline-block bg-safari-light text-safari-green px-3 py-1 rounded-full text-xs mr-2 mb-2"
                                    >
                                        {highlight}
                                    </span>
                                ))}
                            </div>
                            <div className="btn-secondary w-full block text-center mt-auto">
                                View Details
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-safari-green hover:text-white text-safari-green rounded-full p-3 shadow-lg transition-all duration-300 z-10 group hidden md:block"
                aria-label="Next safari experience"
            >
                <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                    />
                </svg>
            </button>

            {/* Auto-play toggle */}
            <div className="flex justify-center mt-6">
                <button
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className="text-sm text-gray-600 hover:text-safari-green transition-colors flex items-center gap-2"
                >
                    {isAutoPlaying ? (
                        <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                            </svg>
                            Pause Auto-Scroll
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                            Play Auto-Scroll
                        </>
                    )}
                </button>
            </div>

            {/* Add CSS to hide scrollbar */}
            <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
        </div>
    )
}
